// .src/components/features/DisplayLayouts/DisplayLayoutSelector/DisplayLayoutSelector.tsx
import React, { useMemo, useEffect, useRef } from "react";
import { Select } from "@mantine/core";
import { useDisplayLayouts } from "@/hooks/useDisplayLayouts";
import { LoadingIndicator } from "@components/generic/LoadingIndicator";
import { DisplayLayout } from "@/utils/api/DisplayLayoutService";
import { useApp } from "@/contexts";

export interface DisplayLayoutSelectorProps {
  /** Selected display layout ID */
  value: string | null;

  /** Callback when display layout selection changes */
  onChange?: (layoutId: string | null, layout?: DisplayLayout) => void;

  /** Venue ID to filter display layouts by */
  venueId?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive display layouts */
  includeInactive?: boolean;

  /** Custom filter function for display layouts */
  layoutFilter?: (layout: DisplayLayout) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select */
  [key: string]: any;
}

export const DisplayLayoutSelector: React.FC<DisplayLayoutSelectorProps> = ({
  value,
  onChange,
  venueId,
  placeholder = "Select a display layout",
  includeInactive = false,
  layoutFilter,
  required = false,
  loadingPlaceholder = "Loading display layouts...",
  emptyText = "No display layouts available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  const { isAuthenticated, user } = useApp();
  const { displayLayouts, loading, isEmpty, lastError } = useDisplayLayouts({
    autoLoad: true,
    isAuthenticated,
    userId: user?.id,
  });

  const hasTriggeredInitialRef = useRef(false);

  // Filter and prepare display layout options
  const layoutOptions = useMemo(() => {
    let filteredLayouts = displayLayouts;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredLayouts = filteredLayouts.filter(
        (layout) => layout.status === 100 // Only show active layouts
      );
    }

    // Apply custom filter if provided
    if (layoutFilter) {
      filteredLayouts = filteredLayouts.filter(layoutFilter);
    }

    // Sort by name for better UX
    filteredLayouts.sort((a, b) => {
      const nameA = a.name || "Unnamed Layout";
      const nameB = b.name || "Unnamed Layout";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredLayouts.map((layout) => ({
      value: layout.id || "",
      label: layout.name || "Unnamed Layout",
      disabled: layout.status === 0, // Disabled layouts have status 0
    }));
  }, [displayLayouts, includeInactive, layoutFilter]);

  // Create a map for quick layout lookup by ID
  const layoutMap = useMemo(() => {
    const map = new Map();
    displayLayouts.forEach((layout) => {
      if (layout.id) {
        map.set(layout.id, layout);
      }
    });
    return map;
  }, [displayLayouts]);

  // Trigger initial onChange if value is already set when layouts load
  useEffect(() => {
    if (
      !loading &&
      !hasTriggeredInitialRef.current &&
      value &&
      layoutMap.has(value) &&
      onChange
    ) {
      const selectedLayout = layoutMap.get(value);
      onChange(value, selectedLayout);
      hasTriggeredInitialRef.current = true;
    }
  }, [loading, value, layoutMap, onChange]);

  // Auto-select first layout if required and no value is set
  useEffect(() => {
    if (
      required &&
      !value &&
      layoutOptions.length > 0 &&
      !loading &&
      onChange
    ) {
      const firstLayout = layoutOptions[0];
      if (firstLayout && !firstLayout.disabled) {
        const selectedLayout = layoutMap.get(firstLayout.value);
        onChange(firstLayout.value, selectedLayout);
      }
    }
  }, [required, value, layoutOptions, loading, onChange, layoutMap]);

  // Handle onChange with layout object lookup
  const handleChange = (selectedLayoutId: string | null) => {
    if (selectedLayoutId) {
      const selectedLayout = layoutMap.get(selectedLayoutId);
      onChange?.(selectedLayoutId, selectedLayout);
    } else {
      // When clearing selection, pass null for both ID and layout
      onChange?.(null, undefined);
    }
  };

  // Determine the current state and configure select props accordingly
  const getSelectState = () => {
    // Loading state
    if (loading && displayLayouts.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={size} color="gray" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading display layouts",
        disabled: true,
        error: error || "Failed to load display layouts",
      };
    }

    // Empty state
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: true,
      };
    }

    // Normal state with data
    return {
      data: layoutOptions,
      placeholder,
      disabled: disabled || loading,
      rightSection:
        loading && displayLayouts.length > 0 ? (
          <LoadingIndicator size={size} color="gray" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const selectState = getSelectState();

  // Render the select component
  return (
    <Select
      {...selectProps}
      size={size}
      data={selectState.data}
      value={value ?? null} // Convert undefined to null
      onChange={handleChange} // Use our custom handler
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error || error}
      rightSection={selectState.rightSection}
    />
  );
};
